// ==============================================
// Chart editing — the pure logic behind the MyPolaris route editor
// ==============================================
//
// Kept free of React and Leaflet so it can be unit-tested directly, and so the
// rules that must match VortexNav live in one readable place.
//
// The naming rules here are a port of the Scout's own
// vortexnav-app/src/utils/routeWaypointNaming.ts. They are not a
// reinterpretation: a skipper who inserts a waypoint on the portal must get the
// same name they would have got on the boat, or the two libraries drift apart.

export type LatLon = [number, number];

export interface EditPoint {
  lat: number;
  lon: number;
  /** Route waypoints carry a name; a bare track point does not. */
  name: string;
}

// ---- geometry ---------------------------------------------------------

const R_NM = 3440.065;
const D2R = Math.PI / 180;

export function distanceNm(a: EditPoint | LatLon, b: EditPoint | LatLon): number {
  const [aLat, aLon] = Array.isArray(a) ? a : [a.lat, a.lon];
  const [bLat, bLon] = Array.isArray(b) ? b : [b.lat, b.lon];
  const dLat = (bLat - aLat) * D2R;
  const dLon = (bLon - aLon) * D2R;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * D2R) * Math.cos(bLat * D2R) * Math.sin(dLon / 2) ** 2;
  return R_NM * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function totalNm(points: Array<EditPoint | LatLon>): number {
  let t = 0;
  for (let i = 0; i < points.length - 1; i++) t += distanceNm(points[i], points[i + 1]);
  return t;
}

/** Initial great-circle bearing, 0–360. */
export function bearingDeg(a: EditPoint, b: EditPoint): number {
  const p1 = a.lat * D2R;
  const p2 = b.lat * D2R;
  const dl = (b.lon - a.lon) * D2R;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (Math.atan2(y, x) * (180 / Math.PI) + 360) % 360;
}

// ---- inserted-waypoint naming (port of VN's routeWaypointNaming.ts) ----
//
//   • Insert toward the NEXT point   -> letter increments:  WP1 -> WP1a -> WP1b
//   • Insert toward the PREVIOUS one -> nest:               WP1a -> WP1aa
//   • Past 'z'                       -> overflow digits:    WP1z -> WP1z1
//   • Extending past the END uses the next whole number (see nextExtendName).

const SUFFIX_RE = /^(.*?)([a-z]+)(\d*)$/;

/** True when `longer` is `shorter` plus a suffix we would have generated. */
export function isDescendant(longer: string, shorter: string): boolean {
  if (longer.length <= shorter.length || !longer.startsWith(shorter)) return false;
  return /^[a-z]+\d*$/.test(longer.slice(shorter.length));
}

function bumpSuffix(letters: string, digits: string): { letters: string; digits: string } {
  if (digits) return { letters, digits: String(Number(digits) + 1) };
  const last = letters[letters.length - 1];
  if (last === 'z') return { letters, digits: '1' };
  return { letters: letters.slice(0, -1) + String.fromCharCode(last.charCodeAt(0) + 1), digits: '' };
}

/**
 * Name for a waypoint inserted BETWEEN `prevName` and `nextName`.
 *
 * The suffix hangs off the preceding neighbour's full name, so this also works
 * for imported routes with arbitrary point names — inserting after Navionics'
 * "WP744c0377-000" yields "WP744c0377-000a".
 */
export function insertedWaypointName(prevName: string, nextName: string | null, taken: readonly string[]): string {
  const used = new Set(taken);
  const candidates: string[] = [];

  const m = SUFFIX_RE.exec(prevName);
  const nextIsChild = !!nextName && isDescendant(nextName, prevName);

  // Sibling first — unless the next point is already our own child, in which
  // case a sibling would sort past it and we must nest instead.
  if (m && !nextIsChild) {
    let s = bumpSuffix(m[2], m[3]);
    for (let i = 0; i < 60; i++) {
      candidates.push(m[1] + s.letters + s.digits);
      s = bumpSuffix(s.letters, s.digits);
    }
  }
  // Then nest deeper: prev + 'a', prev + 'aa', …
  let nest = 'a';
  for (let i = 0; i < 60; i++) {
    candidates.push(prevName + nest);
    nest += 'a';
  }
  for (const c of candidates) if (!used.has(c)) return c;
  return `${prevName}-${used.size + 1}`;
}

/**
 * Name for a waypoint appended past an END of the route. VN uses the next whole
 * number here rather than a letter suffix — the point is not between anything.
 */
export function nextExtendName(routeName: string, taken: readonly string[]): string {
  const used = new Set(taken);
  for (let n = taken.length + 1; n < taken.length + 500; n++) {
    const candidate = `${routeName} - WP${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${routeName} - WP${Date.now()}`;
}

/** Sequential names for a freshly drawn route: "<name> - WP1", "WP2", … */
export function drawnWaypointName(routeName: string, index: number): string {
  return `${routeName} - WP${index + 1}`;
}

// ---- Save As naming ---------------------------------------------------

/** "Test 3" -> "Test 3 (v2)"; "Test 3 (v2)" -> "Test 3 (v3)". */
export function suggestSaveAsName(name: string, taken: readonly string[] = []): string {
  const used = new Set(taken);
  const m = /^(.*?)\s*\(v(\d+)\)$/.exec(name);
  const stem = m ? m[1] : name;
  let n = m ? Number(m[2]) + 1 : 2;
  for (let i = 0; i < 200; i++, n++) {
    const candidate = `${stem} (v${n})`;
    if (!used.has(candidate)) return candidate;
  }
  return `${stem} (copy)`;
}

// ---- track -> route thinning -----------------------------------------

/** Perpendicular distance from p to the segment a→b, in degrees-ish units. */
function perpDistance(p: LatLon, a: LatLon, b: LatLon): number {
  // Flat-earth is fine here: we only need a *relative* measure to rank points,
  // and a track's span is small compared with the curvature of the earth.
  const scale = Math.cos(((a[0] + b[0]) / 2) * D2R) || 1;
  const px = p[1] * scale, py = p[0];
  const ax = a[1] * scale, ay = a[0];
  const bx = b[1] * scale, by = b[0];
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** Ramer–Douglas–Peucker. Keeps corners, discards straight-line filler. */
export function simplify(points: readonly LatLon[], tolerance: number): LatLon[] {
  if (points.length <= 2) return [...points];
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length) {
    const [first, last] = stack.pop()!;
    let maxD = -1;
    let idx = -1;
    for (let i = first + 1; i < last; i++) {
      const d = perpDistance(points[i], points[first], points[last]);
      if (d > maxD) { maxD = d; idx = i; }
    }
    if (maxD > tolerance && idx > 0) {
      keep[idx] = 1;
      stack.push([first, idx], [idx, last]);
    }
  }
  return points.filter((_, i) => keep[i] === 1);
}

/** The band a converted route should land in, per the agreed design. */
export const THIN_TARGET_MIN = 20;
export const THIN_TARGET_MAX = 40;
export const THIN_TARGET_DEFAULT = 28;

/**
 * Thin a recorded track down to roughly `target` turning points.
 *
 * Douglas–Peucker rather than "keep every Nth point", because a route is a plan
 * a human reads: it should keep the corners where the vessel actually altered
 * course and throw away the long straight runs, which is the opposite of what
 * uniform sampling does. The tolerance is found by bisection since there is no
 * closed form from tolerance to point count.
 */
export function thinToTarget(points: readonly LatLon[], target: number): LatLon[] {
  if (points.length <= 2) return [...points];
  const want = Math.max(2, Math.min(target, points.length));
  if (points.length <= want) return [...points];

  let lo = 0;
  let hi = 1;
  // Grow hi until it over-simplifies, so the answer is bracketed.
  for (let i = 0; i < 40 && simplify(points, hi).length > want; i++) hi *= 2;

  let best = simplify(points, hi);
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    const out = simplify(points, mid);
    if (out.length > want) {
      lo = mid;
    } else {
      best = out;
      hi = mid;
      if (out.length === want) break;
    }
  }
  return best;
}

// ---- undo / redo ------------------------------------------------------

export interface History<T> {
  past: T[];
  future: T[];
}

export const emptyHistory = <T,>(): History<T> => ({ past: [], future: [] });

/** Depth cap. Generous for a desk session, bounded so a long edit cannot grow
 *  without limit while the tab stays open. */
export const HISTORY_LIMIT = 200;

export function pushHistory<T>(h: History<T>, snapshot: T): History<T> {
  const past = [...h.past, snapshot];
  if (past.length > HISTORY_LIMIT) past.shift();
  // Any new action abandons the redo branch — standard, and it keeps the model
  // honest: you cannot redo into a future that no longer follows from the past.
  return { past, future: [] };
}

export function undo<T>(h: History<T>, current: T): { history: History<T>; value: T } | null {
  if (!h.past.length) return null;
  const past = h.past.slice(0, -1);
  return { history: { past, future: [current, ...h.future] }, value: h.past[h.past.length - 1] };
}

export function redo<T>(h: History<T>, current: T): { history: History<T>; value: T } | null {
  if (!h.future.length) return null;
  const [next, ...future] = h.future;
  return { history: { past: [...h.past, current], future }, value: next };
}

// ---- library visibility ------------------------------------------------

/**
 * Would the library's current filters hide this element from the list?
 *
 * Creating something and not seeing it appear is indistinguishable from the
 * save having failed, so the page uses this to relax whichever filter would
 * have swallowed a newly created item.
 */
export function isHiddenByFilters(
  el: { type: string; name: string; tags?: readonly string[] },
  types: ReadonlySet<string>,
  query: string,
): boolean {
  if (!types.has(el.type)) return true;
  const needle = query.trim().toLowerCase();
  if (!needle) return false;
  const inName = el.name.toLowerCase().includes(needle);
  const inTags = (el.tags ?? []).some((t) => t.toLowerCase().includes(needle));
  return !inName && !inTags;
}

// ---- conversions to and from the wire shape ---------------------------

export function toEditPoints(
  points: readonly LatLon[],
  names: readonly (string | null)[] | null | undefined,
  fallbackName: string,
  type: 'route' | 'track' | 'waypoint' = 'route',
): EditPoint[] {
  // A standalone waypoint is one point, and its label IS the element's name.
  // Calling it "Gulf Harbour ent. - WP1" would be nonsense, and it also made
  // renaming look broken: the editor showed a manufactured name, and the real
  // one never changed.
  const single = type === 'waypoint' && points.length === 1;
  return points.map(([lat, lon], i) => ({
    lat,
    lon,
    name: names?.[i] ?? (single ? fallbackName : drawnWaypointName(fallbackName, i)),
  }));
}

export function toWire(points: readonly EditPoint[]): { points: LatLon[]; waypoint_names: string[] } {
  return {
    points: points.map((p) => [p.lat, p.lon] as LatLon),
    waypoint_names: points.map((p) => p.name),
  };
}
